import { v4 as uuidv4 } from 'uuid';
import type { SearchParams } from '@/server/searchParams';
import { Country, SearchModes } from '@/domainLogic/index.ts';
import { URLParameters } from '@/app/common/urlParameters';
import { PAGE_TYPE } from '@/app/common/pageType';
import { areTaxesIncludedInPrice } from '@/app/common/config/configuration/configurationUtilsClient';
import type { PageWrapperData } from '../common/createPageWrapperData';
import type { InitialParams } from '../components/hotel-detail/InitialParamsProvider';

export type CreateHotelDetailData = {
  hotelId: string;
  transportId: string | undefined;
  pageWrapperData: PageWrapperData;
  searchParams: SearchParams;
  isHotelStaticPage?: boolean;
};

export type BrandedProps = {
  debugData?: DebugProps[];
  initialParams: InitialParams;
};

export const createHotelDetailData = ({
  hotelId,
  transportId,
  pageWrapperData,
  searchParams,
  isHotelStaticPage = false,
}: CreateHotelDetailData): BrandedProps => {
  const { searchMode, searchId } = pageWrapperData.searchInfoData;

  const hotelDetailProps: BrandedProps = {
    initialParams: {
      env: process.env.ENV,
      // MPH-935: this read `SearchModes.DP && …`, a non-empty string constant that is always truthy, so
      // atol was set for every GB search including Hotel Only — which carries no ATOL protection. The
      // perimeter mirrors the results page (SortBarInfoBadges): packages and train+hotel, in GB only.
      atol:
        (searchMode === SearchModes.DP || searchMode === SearchModes.TH) &&
        pageWrapperData.localization.country === Country.GB,
      internalHotelId: Number(hotelId),
      priceType: searchMode === SearchModes.HO ? 'TOTAL' : 'PER_PERSON',
      taxesIncludedInPrice: areTaxesIncludedInPrice({
        searchMode,
        country: pageWrapperData.localization.country,
        configValuesApp: pageWrapperData.localization.configValuesApp,
      }),
      pageType: searchMode === SearchModes.HO ? 'ho' : searchMode === SearchModes.TH ? 'th' : 'dp',
      // this will be regenerated for every new hotel and new search
      productDetailKey: uuidv4(),
      // hdpSearchId will be used to know if we need to create a new productDetailKey, in cases when the search has changed but the user selects the same hotel.
      hdpSearchId: searchId,
      // TODO
      partnerUrl: '',
      // TODO config parameter
      tshUrl: 'https://www.lastminute.com/terms/top-secret-hotels.html',
      transportId,
      // MPH-646: in Evo the flightClickId identifies one flight-viewing session — all events fired
      // while a flight selection is shown (CHOOSE_FLIGHT_SHOWN, BRANDED_FARES_*, the book-click
      // funnel events, CLICK2CHECKOUT) join on it. This seed covers direct SSR landings on the
      // flight/review steps; a new session id is rotated in on every flight-page entry
      // (rotateEvoFlightClickId) and flight change (useChangeTransportHandler).
      // Not seeded for hotel-only (no flights) nor classic (rotated per flight click there).
      ...(pageWrapperData.localization.configValuesApp.evolutionEnabled && searchMode !== SearchModes.HO
        ? { flightClickId: uuidv4().slice(0, 32) }
        : undefined),
      actionIdSeed: uuidv4(),
      isHotelStaticPage,
      rateApex: !!searchParams[URLParameters.RATE_APEX],
      ratePriceContext: searchParams[URLParameters.RATE_PRICE_CONTEXT],
      ratePriceContextSignature: searchParams[URLParameters.RATE_PRICE_CONTEXT_SIGNATURE],
      // In EVO layout we wait for the apex calendar to settle before calling pricing-id,
      // so it can use ratesCollection or context/signature from the calendar response.
      // Only defer on hotelDetail — transport detail and review pages don't render the apex calendar.
      isApexCalendarReady:
        !pageWrapperData.localization.configValuesApp.evolutionEnabled ||
        searchParams[URLParameters.PAGE_TYPE] !== PAGE_TYPE.HOTEL_DETAIL,
    },
  };

  // TODO Temp: to TEST hotel_only = true
  // props.configexperiments['double-price'] = true;
  return hotelDetailProps;
};
